import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CarModelService } from 'src/app/services/car-model/car-model.service';
import { CarModelModalService } from 'src/app/services/car-model-modal/car-model-modal.service';
import { CarMakerService } from 'src/app/services/car-maker/car-maker.service';
import { VehicleTypeService } from 'src/app/services/vehicle-type/vehicle-type.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-car-models',
  templateUrl: './add-edit-car-models.component.html',
  styleUrls: ['./add-edit-car-models.component.scss']
})
export class AddEditCarModelsComponent extends DestructorComponent implements OnInit {

  @Output() carModelAdded = new EventEmitter<void>();
  @Output() carModelUpdated = new EventEmitter<void>();

  carModelForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedCarModel: any = null;

  // Dropdown data
  carMakers: any[] = [];
  vehicleTypes: any[] = [];
  isLoadingCarMakers: boolean = false;
  isLoadingVehicleTypes: boolean = false;

  constructor(
    private fb: FormBuilder,
    private _carModelService: CarModelService,
    private _carModelModalService: CarModelModalService,
    private _carMakerService: CarMakerService,
    private _vehicleTypeService: VehicleTypeService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
    this.loadCarMakers();
    this.loadVehicleTypes();
  }

  private initializeForm(): void {
    this.carModelForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      m3: ['', [Validators.required]],
      parent: ['', [Validators.required]],
      vehicle_type: ['', [Validators.required]]
    });
  }

  // Getter methods for form controls
  get name() { return this.carModelForm.get('name'); }
  get m3() { return this.carModelForm.get('m3'); }
  get parent() { return this.carModelForm.get('parent'); }
  get vehicle_type() { return this.carModelForm.get('vehicle_type'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_car_model_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceCarModel = this._carModelModalService.getSelectedCarModel();
        if (serviceCarModel) {
          this.isEditMode = true;
          this.selectedCarModel = serviceCarModel;
          this.populateForm(this.selectedCarModel);
        } else {
          this.isEditMode = false;
          this.selectedCarModel = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._carModelModalService.clearSelectedCarModel();
        this.resetForm();
      });
    }
  }

  private loadCarMakers(): void {
    this.isLoadingCarMakers = true;
    this.subscriptions.push(
      this._carMakerService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.carMakers = response.data.data || response.data || [];
          } else {
            this.carMakers = [];
          }
          this.isLoadingCarMakers = false;
        },
        error: (error) => {
          console.error('Error loading car makers:', error);
          this.carMakers = [];
          this.isLoadingCarMakers = false;
        }
      })
    );
  }

  private loadVehicleTypes(): void {
    this.isLoadingVehicleTypes = true;
    this.subscriptions.push(
      this._vehicleTypeService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.vehicleTypes = response.data.data || response.data || [];
          } else {
            this.vehicleTypes = [];
          }
          this.isLoadingVehicleTypes = false;
        },
        error: (error) => {
          console.error('Error loading vehicle types:', error);
          this.vehicleTypes = [];
          this.isLoadingVehicleTypes = false;
        }
      })
    );
  }

  private populateForm(carModel: any): void {
    if (carModel) {
      this.carModelForm.patchValue({
        name: carModel.name || '',
        m3: carModel.m3 || '',
        parent: carModel.maker_slug || '',
        vehicle_type: carModel.vehicle_type?._id || carModel.vehicle_type?.id || carModel.vehicle_type || ''
      });
    }
  }

  private resetForm(): void {
    this.carModelForm.reset();
    this.isLoading = false;
    this.isEditMode = false;
    this.selectedCarModel = null;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_car_model_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.carModelForm.invalid) {
      this.markFormGroupTouched(this.carModelForm);
      return;
    }

    this.isLoading = true;

    const formData = {
      name: this.carModelForm.get('name')?.value,
      m3: this.carModelForm.get('m3')?.value,
      parent: this.carModelForm.get('parent')?.value,
      vehicle_type: this.carModelForm.get('vehicle_type')?.value
    };

    if (this.isEditMode) {
      this.updateCarModel(formData);
    } else {
      this.addCarModel(formData);
    }
  }

  private addCarModel(formData: any): void {
    this.subscriptions.push(
      this._carModelService.AddData(formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Car model added successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.carModelAdded.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add car model.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error adding car model:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while adding the car model.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private updateCarModel(formData: any): void {
    const carModelId = this.selectedCarModel._id || this.selectedCarModel.id;
    this.subscriptions.push(
      this._carModelService.UpdateData(carModelId, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Car model updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.carModelUpdated.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update car model.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error updating car model:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while updating the car model.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }
}
